import React from 'react';
import clsx from 'clsx';
import { makeStyles } from '@material-ui/styles';
import {
  Button,
  IconButton,
  Card,
  CardHeader,
  CardContent,
  Divider,
  Grid,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  CircularProgress,
  Typography
} from '@material-ui/core';
import Skeleton from 'react-loading-skeleton';
import { SkeletonCardFields } from 'components/Loading';
import { filterValidQueryTemplates } from 'views/Campaign/helpers';
import useRecipientQueries from 'views/RecipientQueries/useRecipientQueries';
import { Add, Delete } from '@material-ui/icons';
import { useDispatch } from 'react-redux';
import { Details, Design } from 'views/RecipientQueries/components';
import CloseIcon from '@material-ui/icons/Close';
import { actions } from 'store/recipientQueryTemplate';
import { SelectInput } from 'components';

const { unsetNextStep, unsetCurrentQueryTemplate } = actions;
const useStyles = makeStyles(theme => ({
  root: {},
  content: {
    padding: theme.spacing(3)
  },
  button: {
    margin: theme.spacing(1)
  },
  deleteButton: {
    color: theme.palette.error.main
  },
  containerMargin: {
    marginLeft: theme.spacing(3)
  },
  selectContainer: {
    display: 'inline',
    minWidth: '14rem',
    margin: theme.spacing(1)
  },
  buttonContainer: {
    display: 'inline',
    margin: theme.spacing(1)
  },
  ruleError: {
    color: theme.palette.error.main,
    paddingTop: '13px'
  },
  dateField: {
    width: '100%',
    marginTop: '0px'
  },
  templateButtonError: {
    paddingLeft: '19px',
    color: theme.palette.error.main
  },
  ruleDivider: {
    marginTop: '10px',
    marginBottom: '10px'
  },
  dialogSubTitle: {
    fontSize: '14px',
    fontWeight: '400',
    color: '#546e7a',
    letterSpacing: '-0.05px'
  },
  closeButton: {
    position: 'absolute',
    right: theme.spacing(1),
    top: theme.spacing(1),
    color: theme.palette.grey[500]
  },
  nextButton: {
    marginRight: '16px',
    marginBottom: '8px'
  }
}));

const CampaignRecipientQuery = props => {
  const { className, campaign, queryTemplates, ...rest } = props;
  const {
    thisCampaign,
    handleChange,
    onBlur,
    onFocus,
    shouldShowError,
    getErrorMessage,
    isLoading,
    isCampaignReadOnly
  } = campaign;

  const classes = useStyles();

  const [open, setOpen] = React.useState(false);
  const template = useRecipientQueries();
  const { isInTemplateDesignDialog } = template;
  const dispatch = useDispatch();
  const openCreateRecipientDialog = () => {
    setOpen(true);
    campaign.setCurrentRecipientDetails();
  };
  const closeCreateRecipientDialog = () => {
    setOpen(false);
    template.resetValidationErrors();
    dispatch(unsetNextStep());
    dispatch(unsetCurrentQueryTemplate());
  };
  const SkeletonCardContent = props => {
    return (
      <CardContent>
        <Grid container spacing={0}>
          <Grid item xs={4}>
            <Skeleton />
          </Grid>
          <SkeletonCardFields numberOfRows={5} />
        </Grid>
      </CardContent>
    );
  };

  if (thisCampaign.campaignOccurrenceType !== 'ON_DEMAND') {
    return null;
  }

  const validQueryTemplates = {
    list: filterValidQueryTemplates(queryTemplates, thisCampaign),
    keyId: 'uuid',
    valueId: 'uuid',
    displayValueId: 'name'
  };

  return (
    <div className={classes.root}>
      <Card {...rest} className={clsx(classes.root, className)}>
        <form autoComplete="off">
          <CardHeader
            subheader="* indicates a required field"
            title="Campaign Recipient Query"
            titleTypographyProps={{ variant: 'h1' }}
          />
          <Divider />
          {isLoading && <SkeletonCardContent />}
          {!isLoading && (
            <CardContent>
              <Grid container spacing={0}>
                <SelectInput
                  fullWidth
                  label="Select a recipient query"
                  margin="dense"
                  name="campaignRecipientQueryUuid"
                  disabled={isCampaignReadOnly()}
                  onChange={handleChange}
                  onFocus={onFocus}
                  onBlur={onBlur}
                  required
                  value={thisCampaign.campaignRecipientQueryUuid || ''}
                  error={shouldShowError('campaignRecipientQueryUuid')}
                  helperText={getErrorMessage('campaignRecipientQueryUuid')}
                  options={validQueryTemplates}
                />
              </Grid>

              {!isCampaignReadOnly() && (
                <Button
                  variant="contained"
                  color="primary"
                  className={classes.button}
                  startIcon={<Add />}
                  onClick={openCreateRecipientDialog}>
                  Create Recipient Query
                </Button>
              )}
              {!isInTemplateDesignDialog ? (
                <Dialog
                  open={open}
                  onClose={closeCreateRecipientDialog}
                  aria-labelledby="form-dialog-title"
                  fullWidth
                  maxWidth="lg">
                  <Typography variant="h2">
                    <DialogTitle
                      id="form-dialog-title"
                      disableTypography={true}>
                      <div>Recipient Query Details</div>
                      <div className={classes.dialogSubTitle}>
                        * indicates a required field
                      </div>
                    </DialogTitle>
                    <IconButton
                      aria-label="close"
                      className={classes.closeButton}
                      onClick={closeCreateRecipientDialog}>
                      <CloseIcon />
                    </IconButton>
                  </Typography>
                  <DialogContent>
                    {template.isLoading && (
                      <Box style={{ width: '50%' }}>
                        {' '}
                        <CircularProgress style={{ float: 'right' }} />
                      </Box>
                    )}
                    {!template.isLoading && (
                      <Details
                        isPopupDialog={true}
                        template={template}></Details>
                    )}
                  </DialogContent>
                  <DialogActions>
                    <Button
                      className={classes.nextButton}
                      onClick={template.goNext}
                      disabled={template.isLoading}
                      color="primary"
                      variant="contained">
                      Next
                    </Button>
                  </DialogActions>
                </Dialog>
              ) : (
                <Dialog
                  variant="h2"
                  open={open}
                  onClose={closeCreateRecipientDialog}
                  aria-labelledby="form-dialog-title"
                  fullWidth
                  maxWidth="lg">
                  <Typography variant="h2">
                    <DialogTitle
                      id="form-dialog-title"
                      disableTypography={true}>
                      <div>Recipient Queries</div>
                    </DialogTitle>
                    <IconButton
                      aria-label="close"
                      className={classes.closeButton}
                      onClick={closeCreateRecipientDialog}>
                      <CloseIcon />
                    </IconButton>
                  </Typography>
                  <DialogContent>
                    <Design isPopupDialog={true} template={template}></Design>
                  </DialogContent>
                  <DialogActions>
                    <Button
                      className={classes.backButton}
                      onClick={template.goBack}
                      disabled={template.isLoading || template.isTemplateSaving}
                      color="secondary"
                      variant="contained">
                      Back
                    </Button>
                    <Box flexGrow={1}>&nbsp;</Box>
                  </DialogActions>
                </Dialog>
              )}
            </CardContent>
          )}
        </form>
      </Card>
    </div>
  );
};

export default CampaignRecipientQuery;
